import type { TalentDef } from '../../data/schema';
import { ZERO_META_BONUSES, type MetaBonuses } from './bonuses';

export type TalentRanks = Record<string, number>;
type Defs = Record<string, TalentDef>;

/** Coût du prochain rang ; `rank` est le rang déjà possédé. */
export function nextCost(def: TalentDef, rank: number): number {
  return Math.round(def.cost * Math.pow(def.costGrowth, rank));
}

export function canBuy(defs: Defs, ranks: TalentRanks, id: string, reflets: number): 'ok' | 'inconnu' | 'max' | 'prerequis' | 'reflets' {
  const def = defs[id];
  if (!def) return 'inconnu';
  const rank = ranks[id] ?? 0;
  if (rank >= def.maxRank) return 'max';
  if (!def.requires.every((r) => (ranks[r] ?? 0) >= 1)) return 'prerequis';
  if (reflets < nextCost(def, rank)) return 'reflets';
  return 'ok';
}

export function buyTalent(defs: Defs, ranks: TalentRanks, id: string, reflets: number): { ranks: TalentRanks; reflets: number } | null {
  if (canBuy(defs, ranks, id, reflets) !== 'ok') return null;
  const rank = ranks[id] ?? 0;
  return { ranks: { ...ranks, [id]: rank + 1 }, reflets: reflets - nextCost(defs[id]!, rank) };
}

/** Somme des coûts payés rang par rang (ids inconnus ignorés, rang borné à maxRank). */
export function totalSpent(defs: Defs, ranks: TalentRanks): number {
  let sum = 0;
  for (const [id, rank] of Object.entries(ranks)) {
    const def = defs[id];
    if (!def) continue;
    for (let r = 0; r < Math.min(rank, def.maxRank); r++) sum += nextCost(def, r);
  }
  return sum;
}

export function resetTalents(defs: Defs, ranks: TalentRanks, reflets: number): { ranks: Record<string, never>; reflets: number } {
  return { ranks: {}, reflets: reflets + totalSpent(defs, ranks) };
}

/**
 * Bonus cumulés : ZERO + rang * valeur par effet ; startItem et startSpell une seule fois par nœud (rang >= 1).
 * Parcours dans l'ordre du contenu (`defs`) : l'ordre des objets et sorts de départ ne dépend pas de l'ordre des clés de `ranks`.
 */
export function bonusesFrom(defs: Defs, ranks: TalentRanks): MetaBonuses {
  const b: MetaBonuses = { ...ZERO_META_BONUSES, startItems: [], startSpells: [], roomModifiers: [] };
  for (const def of Object.values(defs)) {
    const raw = Object.hasOwn(ranks, def.id) ? ranks[def.id]! : 0;
    if (typeof raw !== 'number' || Number.isNaN(raw)) continue;
    const rank = Math.min(Math.max(0, Math.floor(raw)), def.maxRank);
    if (rank < 1) continue;
    for (const e of def.effects) {
      if (e.type === 'startItem') b.startItems.push(e.slot);
      else if (e.type === 'startSpell') b.startSpells.push(e.spellId);
      else b[e.type] += rank * e.value;
    }
  }
  return b;
}
